import { useState } from "react";
import { useSearch } from "wouter";

import { useDebouncedValue } from "../../../../hooks/useDebouncedValue";
import {
  AdminJob,
  AdminJobMinAgeHours,
  AdminJobsArgs,
  useGetAdminJobsQuery,
} from "../../../../services";
import { toSectionState } from "../../utils/sectionState";
import {
  JOB_SEARCH_DEBOUNCE_MS,
  JOB_SEARCH_MAX_LENGTH,
  JOB_STATE_OPTIONS,
} from "../constants";
import { JobAgeFilter, JobStateFilter } from "../types";
import { keyOfJob } from "../utils/describeJob";

const NO_JOBS: AdminJob[] = [];

const stateFromSearch = (search: string): JobStateFilter => {
  const requested = new URLSearchParams(search).get("state");
  const option = JOB_STATE_OPTIONS.find(({ value }) => value === requested);
  return option ? option.value : "all";
};

const toArgs = (
  query: string,
  state: JobStateFilter,
  age: JobAgeFilter,
): AdminJobsArgs => ({
  ...(query && { query }),
  ...(state !== "all" && { state }),
  ...(age !== "any" && { minAgeHours: Number(age) as AdminJobMinAgeHours }),
});

export const useJobs = () => {
  const search = useSearch();
  const [query, setQuery] = useState("");
  const [state, setState] = useState(() => stateFromSearch(search));
  const [age, setAge] = useState<JobAgeFilter>("any");
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const debouncedQuery = useDebouncedValue(
    query.trim(),
    JOB_SEARCH_DEBOUNCE_MS,
  );
  const args = toArgs(debouncedQuery, state, age);
  const jobsQuery = useGetAdminJobsQuery(args);

  // currentData, so a failed read after a filter change never shows the previous filter's rows.
  const section = toSectionState({
    data: jobsQuery.currentData,
    isError: jobsQuery.isError,
  });
  const jobs = section.state === "available" ? section.data.jobs : NO_JOBS;
  const total = section.state === "available" ? section.data.total : 0;
  const selected =
    jobs.find((job) => keyOfJob(job) === selectedKey) ?? jobs[0] ?? null;

  const changeQuery = (next: string) =>
    setQuery(next.slice(0, JOB_SEARCH_MAX_LENGTH));
  const clearFilters = () => {
    setQuery("");
    setState("all");
    setAge("any");
  };

  return {
    section,
    query,
    state,
    age,
    jobs,
    total,
    selected,
    isFiltered: Object.keys(args).length > 0,
    changeQuery,
    changeState: setState,
    changeAge: setAge,
    clearFilters,
    select: setSelectedKey,
    isRefreshing: jobsQuery.isFetching,
    refresh: () => void jobsQuery.refetch(),
  };
};
